import config from '../config';
import {
    StripeCreateCreatorSubscriptionResponse,
    StripeCreatePaymentIntentResponse,
    StripeGetPaymentIntentResponse,
} from './stripe';
import { PaginatedData } from './util';

export const WrapperLicense = '0c318ef2-9f61-4742-bac2-dc6a8b59fa31';
export const StreamGoldId = 'de2861f3-c428-47ac-8288-61c54bf359b1:streamgold';
export const BingoBinglesId = '6196834d-3c86-4bfb-b536-bf1ee821194b:curr_coin';
export const LeviathanDuckatsId = '11f3855f-1e76-40b1-acf6-816382d00974:ingame_currency';
export const LeviathanQuantumId = '11f3855f-1e76-40b1-acf6-816382d00974:premium_currency';

export const TwoFactorError = '2FA not setup';
export const PayoutNotAvailableError = 'Withdrawal is not available at this time';

export interface ItemDef {
    // Item ID
    item_id: string;

    // [optional]
    //
    // Item Name
    item_name: string;

    // [optional]
    //
    // Item description
    item_desc: string;

    // Item type is arbitrary string that provides categorization for items such as `currency`, `weapon`.
    //
    // It can be any value defined by the item's creator.
    //
    // It is mandatory as it is used in filtering queries.
    item_type: string;

    // [optional]
    //
    // Item properties are arbitrary data that item's creator can use to store any info.
    item_prop: {};

    // The user that created this item
    item_owner: string;

    // [optional]
    //
    // Indicate this item is a subscription.
    is_subscription: boolean;

    // [optional]
    //
    // List of games that are granted permission to create a copy of this item into user inventory.
    // Creator is automatically a consumer and a reader.
    creator_license: string[];

    // [optional]
    //
    // List of users that are granted permission to create a copy of this item into user inventory.
    // Creator is automatically a consumer and a reader.
    // These users are typical not developers but likely from community.
    // Developers typically get access by adding their game license to their account.
    creator_user: string[];

    // [optional]
    //
    // List of games that are granted permission to consume a copy of this item from user inventory.
    // Consumer is automatically a reader.
    consumer_license: string[];

    // [optional]
    //
    // List of users that are granted permission to consume a copy of this item from user inventory.
    // Consumer is automatically a reader.
    // These users are typical not developers but likely from community.
    // Developers typically get access by adding their game license to their account.
    consumer_user: string[];

    // [optional]
    //
    // List of games that are granted permission to read this item from user inventory
    reader_license: string[];

    // [optional]
    //
    // List of users that are granted permission to read this item from user inventory
    // These users are typical not developers but likely from community.
    // Developers typically get access by adding their game license to their account.
    reader_user: string[];

    // [optional]
    //
    // Expire time for this item definition in unix timestamp.
    //
    // If value is larger than zero, it is effective and will not be return in query after expire.
    //
    // After expire, no more copy of this item can be created in user inventory.
    expire_time: number;
}

// RealProduct type is for product that cost real money.
//
// During purchase, it validate receipt with 3rd party store.
export const RealProduct: string = 'real';

// VirtualProduct type is for paying one item, such as in game currency, in exchange for another item,
export const VirtualProduct: string = 'virtual';

// RewardProduct type has no cost and gives away item for free.
//
// Only game owner can process this transaction.
export const RewardProduct: string = 'reward';

// PayoutProduct type is used during payout process.
// It provides various conversion rates from virtual item to real-world currency.
export const PayoutProduct: string = 'payout';

export interface ProductDef {
    // Product ID
    product_id: string;

    // [optional]
    //
    // Product Name
    product_name: string;

    // [optional]
    //
    // Product description
    product_desc: string;

    // Product type provides filtering for queries.
    //
    // It also has different behaviour when making a purchase.
    product_type: string;

    // [optional]
    //
    // Defines how much it cost to purchase this product.
    //
    // Cost is only for reference for `real` product type. The payment is done through 3rd party store such as Google/Apple store.
    //
    // Cost is mandatory for `virtual` product type.
    //
    // Cost is ignored for `reward` product type.
    cost: CostDef[];

    // [optional]
    //
    // Defines what user receive in their inventory after purchase.
    items: ProductItemDef[];

    // The user that created this product
    product_owner: string;

    // The game that this product belongs to
    license: string;

    // [optional]
    //
    // Expire time for this product definition in unix timestamp.
    //
    // If value is larger than zero, it is effective and will not be return in query after expire.
    //
    // After expire, no more purchase can be made for this product.
    expire_time: number;
}

export interface CostDef {
    // Indicates the cost is virtual.
    // Type is itemID.
    // This field is only used by virtual product.
    cost_type: string;

    // Indicates the cost is real-world currency.
    // This field is only used by real product from certain stores.
    // Other stores ignore cost entirely and use pricing defined on their platform.
    currency: string;

    // Amount of resource to consume
    quantity: number;
}

export interface ProductItemDef {
    // Item ID that user will receive in their inventory after purchase
    item_id: string;

    // Indicates the user will receive real-world currency from payout of this product.
    // This field is only used by payout product.
    currency: string;

    // Amount of resource to receive
    quantity: number;

    // If true, the item will be created in user's shared inventory
    is_share: boolean;

    // [optional]
    //
    // This item will be added to inventory each time user resubscribe this product from the store.
    //
    // This is only effective when another ProductItemDef in the product Items list is a subscription.
    add_on_resubscribe: boolean;

    // [optional]
    //
    // When this item is created in user inventory, it will be a non-aggregated item.
    //
    // The inventory key will have `transac_id` and `item_index` to make this item unique in the inventory.
    is_unique: boolean;

    // [optional]
    //
    // Defines how long after purchase does the item expiries.
    //
    // The format is size 3 array of [year, month, day].
    //
    // For example [0, 1, 0] means the item will be removed from user's inventory in 1 month after purchase.
    //
    // Setting this automatically marks the item as unique.
    max_age: number[];

    // [optional]
    //
    // Defines exact time of expiry, in Unix timestamp.
    //
    // Effective when value is > zero.
    //
    // This takes priority over `MaxAge`.
    //
    // Setting this automatically marks the item as unique.
    expire_time: number;

    // [Query]
    //
    // When query for product, item definition is included in the result.
    //
    // This field has no effect when upsert product.
    item_def: ItemDef | undefined;
}

export interface InventoryKey {
    // The user that owns this item
    user_id: string;

    item_type: string;

    item_id: string;

    // [optional]
    //
    // Transaction ID from purchasing this item.
    transac_id: string;

    // [optional]
    //
    // Item's index that correspond to `ProductDef`'s array of `Items`.
    item_index: string;

    // [optional]
    //
    // Indicates the item is in pending state.
    pending: PendingState | undefined;
}

// Item in pending state cannot be transfer or payout.
export interface PendingState {
    // Item will be release after the pending date + HoldingPeriodDays.
    // Date in the format of 2006-01-02.
    start_date: string;

    // Date on which item is to be released.
    // This value is calculated before returning in query because it can change when value of HoldingPeriodDays changes.
    // Date in the format of 2006-01-02.
    release_date: string;

    // Source is where the item come from.
    // Value can be: "purchase" or "donation"
    source: string;
}

export interface InventoryDef {
    inventory_key: InventoryKey;

    // Amount of resource for this item
    quantity: number;

    // Item definition that was copied when the item was purchased.
    //
    // This is a copy and will not update when the source item definition change.
    inventory_item: ItemDef;

    // Stores arbitrary game data for an item.
    //
    // The data is separated by license. The caller must be owner of a license to modify the data under that license.
    game_prop: {};

    // [optional]
    //
    // Expire time for this item in unix timestamp.
    //
    // If value is larger than zero, it is effective and item will not be return in query when it expires.
    //
    // This value is generated from either `MaxAge` or `ExpireDate` field in `ProductItemDef`
    expire_time: number;

    // True if this item is in user's shared inventory.
    is_share: boolean;

    // Code that allows another user to claim this shared item.
    share_code: string;

    // User that has claimed this share item.
    share_holder: string;

    // User that purchased this share item. Same value as `Owner`
    share_purchaser: string;
}

export interface SubscriptionDef {
    user_id: string;
    item_id: string;

    // Indicate the user to subscribe to.
    // Empty value means subscribe to the platform.
    subscribe_target: string;
}

export interface SubscriptionDef {
    subscription_key: SubscriptionDef;

    // Item definition that was copied when the item was purchased.
    //
    // This is a copy and will not update when the source item definition change.
    subscription_item: ItemDef;

    // Stores arbitrary game data for an item.
    //
    // The data is separated by license. The caller must be owner of a license to modify the data under that license.
    game_prop: {};

    // Purchase token from the store's receipt.
    //
    // When inventory-service receives a notification from the store,
    // purchase token is used to find all inventory tied to the subscription,
    // and update expiry for each inventory.
    purchase_token: string;

    // The product ID that was purchased which gave this inventory.
    product_id: string;

    // The product's license that was purchased which gave this inventory.
    license: string;

    // Number of continuous subscription renewal.
    //
    // On initial subscription, this value is zero.
    //
    // On subscription renew, this value is incremented.
    //
    // On any subscription pause/expire/cancel, this value is reset to zero.
    renewal_streak: number;

    // Tracks total number of renewal.
    //
    // This value is never reset when subscription is pause/expire/cancel.
    renewal_total: number;

    // Status of the subscription.
    //
    // The underlying inactive field indicates if subscription is active.
    // However, if this field is null, the subscription can be assumed to be active.
    status: SubscriptionStatus;

    // Expire time in unix timestamp.
    //
    // If value is larger than zero, it is effective and item will not be return in query when it expires.
    //
    // For purchase from real product with store integration,
    // this value is provided either by the store when subscription was purchased or when subscription status changes,
    //
    // For reward or virtual product,
    // this value is generated from either `MaxAge` or `ExpireDate` field in `ProductItemDef`.
    // Currently, this type of subscription cannot be renew.
    expire_time: number;

    // Start time in unix timestamp.
    //
    // If value is larger than zero, it is effective and item will not be return in query before start time.
    //
    // This is used by subscription that are provision before they are effective, such as during tier change.
    //
    // This value is provided by the store when subscription was purchased or when subscription status changes.
    start_time: number;
}

export interface SubscriptionStatus {
    // Last notification event from the store
    last_notification_type: string;

    // Last notification event (subtype) from the store
    last_notification_subtype: string;

    // Store where status are coming from
    store: string;

    // Flag that indicates the subscription is inactive.
    //
    // See last_notification_type for reason of being inactive.
    // It is possible for a subscription to become inactive before it reach expire_time.
    inactive: string;

    // Data specific to a store.
    // This usually contains error to diagnose or display.
    payload: any;
}

export interface PlayStoreSubscription {
    autoRenewing: boolean;
    autoResumeTimeMillis: string;
    countryCode: string;
    expiryTimeMillis: string;
    kind: string;
    orderId: string;
    paymentState: number;
    priceAmountMicros: string;
    priceCurrencyCode: string;
    purchaseType: number;
    startTimeMillis: string;
}

export interface AppStoreSubscription {
    transactionId: string;
    originalTransactionId: string;
    webOrderLineItemId: string;
    bundleId: string;
    productId: string;
    subscriptionGroupIdentifier: string;
    purchaseDate: number;
    originalPurchaseDate: number;
    expiresDate: number;
    quantity: number;
    type: string;
    inAppOwnershipType: string;
    signedDate: number;
    environment: string;
}

export interface HTTPGetSubscriptionSuccess {
    type: string;
    message: string;
    data: SubscriptionDef[];
    page: PaginatedData;
}

export const getItemByIDs = async (item_ids: string[]): Promise<ItemDef[]> => {
    let url = `${config.inventoryServiceURL}/item?item_ids=${item_ids.toString()}`;
    const resp = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get items');
    }

    const data = await resp.json();
    return data.data;
};

export const getProductByType = async (
    productType: string,
    license: string,
    token: string,
): Promise<ProductDef[]> => {
    let url = `${config.inventoryServiceURL}/product?license=${license}&product_type=${productType}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get product');
    }

    const data = await resp.json();
    return data.data;
};

export const getProductByIDs = async (
    productIDs: string[],
    license: string,
    token: string,
): Promise<ProductDef[]> => {
    if (productIDs.length === 0) {
        return [];
    }

    let url = `${
        config.inventoryServiceURL
    }/product?license=${license}&product_ids=${productIDs.toString()}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get product');
    }

    const data = await resp.json();
    return data.data;
};

export const getUserInventory = async (
    userID: string,
    itemType: string,
    itemID: string,
    token: string,
): Promise<InventoryDef[]> => {
    let url = `${config.inventoryServiceURL}/inventory?user_id=${userID}`;
    if (itemType !== '') {
        url += `&item_type=${itemType}`;
    }
    if (itemID !== '') {
        url += `&item_id=${itemID}`;
    }
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get inventory');
    }

    const data = await resp.json();
    return data.data;
};

export const getUserSubscription = async (
    userID: string,
    targetID: string,
    itemID: string,
    token: string,
): Promise<SubscriptionDef[]> => {
    let url = `${config.inventoryServiceURL}/subscription?user_id=${userID}`;
    if (targetID !== '') {
        url += `&target_id=${targetID}`;
        if (itemID !== '') {
            url += `&item_id=${itemID}`;
        }
    }
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get subscription');
    }

    const data = await resp.json();
    return data.data;
};

// Get a list of subscribers for the target user.
// Use limit to control pagination.
// Call `getSubscriberWithPaging` with the returned pagination to continue query next page.
export const getSubscribers = async (
    targetID: string,
    limit: number,
    token: string,
): Promise<HTTPGetSubscriptionSuccess> => {
    let url = `${config.inventoryServiceURL}/subscriber?target_id=${targetID}&limit=${limit}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get subscriber');
    }

    const data = await resp.json();
    return data;
};

// Call this function with pagination's next page from `getSubscribers` to continue query next page.
export const getSubscribersWithPaging = async (
    nextPage: string,
    token: string,
): Promise<HTTPGetSubscriptionSuccess> => {
    let url = `${config.inventoryServiceURL}/subscriber?${nextPage}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get subscriber');
    }

    const data = await resp.json();
    return data;
};

// Query if a specific user is a subscriber to the target user.
// If return data is empty, then the user is not a subscriber.
export const getSubscriber = async (
    targetID: string,
    subscriberID: string,
    token: string,
): Promise<SubscriptionDef[]> => {
    let url = `${config.inventoryServiceURL}/subscriber?target_id=${targetID}&subscriber_id=${subscriberID}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get subscriber');
    }

    const data = await resp.json();
    return data.data;
};

export const getItemPurchaseToken = async (
    sku: string,
    currency: string,
    language: string,
    return_url: string,
    token: string,
): Promise<string> => {
    const resp = await fetch(`${config.inventoryServiceURL}/xsolla/item/token`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            sku: sku,
            quantity: 1,
            return_url: return_url,
            currency: currency,
            language: language,
            project_id: config.xsollaProjectID,
            sandbox: config.xsollaSandbox,
        }),
        method: 'POST',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get purchase token');
    }

    const data = await resp.json();
    return data.data.token;
};

export const getSubscriptionPurchaseToken = async (
    plan_id: string,
    product_id: number,
    currency: string,
    language: string,
    return_url: string,
    token: string,
): Promise<string> => {
    const resp = await fetch(`${config.inventoryServiceURL}/xsolla/subscription/token`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            plan_id: `${plan_id}`,
            product_id: `${product_id}`,
            return_url: return_url,
            currency: currency,
            language: language,
            project_id: config.xsollaProjectID,
            sandbox: config.xsollaSandbox,
        }),
        method: 'POST',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get purchase token');
    }

    const data = await resp.json();
    return data.data.token;
};

export const getChangeSubscriptionToken = async (
    plan_id: string,
    product_id: number,
    currency: string,
    language: string,
    return_url: string,
    token: string,
): Promise<string> => {
    const resp = await fetch(`${config.inventoryServiceURL}/xsolla/subscription/token`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            plan_id: `${plan_id}`,
            operation: 'change_plan',
            product_id: `${product_id}`,
            return_url: return_url,
            currency: currency,
            language: language,
            project_id: config.xsollaProjectID,
            sandbox: config.xsollaSandbox,
        }),
        method: 'POST',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get purchase token');
    }

    const data = await resp.json();
    return data.data.token;
};

export const updateSubscription = async (
    subscription_id: string | undefined,
    status: string,
    token: string,
): Promise<void> => {
    if (subscription_id === undefined) {
        return;
    }

    const resp = await fetch(
        `${config.inventoryServiceURL}/xsolla/subscription?project_id=${config.xsollaProjectID}&subscription_id=${subscription_id}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                status: status,
            }),
            method: 'PUT',
        },
    );

    if (resp.status !== 200) {
        throw new Error('failed to update subscription');
    }
};

export const transferItem = async (
    inv: InventoryDef,
    quantity: number,
    newUserID: string,
    token: string,
): Promise<void> => {
    if (inv === undefined || newUserID === undefined) {
        return;
    }

    const resp = await fetch(`${config.inventoryServiceURL}/transfer`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            inventory_key: inv.inventory_key,
            quantity: quantity,
            new_user_id: newUserID,
        }),
        method: 'POST',
    });

    const json = await resp.json();

    if (resp.status !== 200) {
        throw new Error(json.message);
    }
};

export const createPaymentIntent = async (
    productID: string,
    token: string,
): Promise<StripeCreatePaymentIntentResponse> => {
    const resp = await fetch(`${config.inventoryServiceURL}/stripe/payment_intent`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            product_id: productID,
            license: WrapperLicense,
        }),
        method: 'POST',
    });

    if (!resp.ok) {
        if (resp.status === 403) {
            throw new Error(TwoFactorError);
        } else {
            throw new Error('Failed to create payment intent');
        }
    }

    const json = await resp.json();
    return json.data;
};

export const getPaymentIntent = async (
    page: string,
    token: string,
): Promise<StripeGetPaymentIntentResponse> => {
    const query = !!page ? page : '';

    const resp = await fetch(`${config.inventoryServiceURL}/stripe/payment_intent?${query}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        throw new Error('failed to get payment intent');
    }

    const json = await resp.json();
    return json;
};

export const cancelPaymentIntent = async (
    paymentIntentID: string,
    token: string,
): Promise<void> => {
    const query = `payment_intent_id=${paymentIntentID}`;
    const resp = await fetch(`${config.inventoryServiceURL}/stripe/payment_intent?${query}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'DELETE',
    });

    if (resp.status !== 200) {
        throw new Error('failed to create payment intent');
    }
};

export const createCreatorSubscription = async (
    productID: string,
    createID: string,
    token: string,
): Promise<StripeCreateCreatorSubscriptionResponse> => {
    const resp = await fetch(`${config.inventoryServiceURL}/stripe/subscription/creator`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            product_id: productID,
            license: WrapperLicense,
            creator_id: createID,
        }),
        method: 'POST',
    });

    if (!resp.ok) {
        if (resp.status === 403) {
            throw new Error(TwoFactorError);
        } else {
            throw new Error('Failed to create creator subscription');
        }
    }

    const json = await resp.json();
    return json.data;
};

export const createStreamsixSubscription = async (
    productID: string,
    token: string,
): Promise<StripeCreateCreatorSubscriptionResponse> => {
    const resp = await fetch(`${config.inventoryServiceURL}/stripe/subscription/streamsix`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            product_id: productID,
            license: WrapperLicense,
        }),
        method: 'POST',
    });

    if (resp.status !== 200) {
        throw new Error('failed to create streamsix subscription');
    }

    const json = await resp.json();
    return json.data;
};

export const payoutStreamgold = async (productID: string, token: string): Promise<void> => {
    const resp = await fetch(`${config.inventoryServiceURL}/stripe/payout`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            product_id: productID,
        }),
        method: 'POST',
    });

    if (!resp.ok) {
        if (resp.status === 503) {
            throw new Error(PayoutNotAvailableError);
        } else {
            throw new Error('Withdrawal failed');
        }
    }
};

export const getCurrUrl = (): string => {
    // return to current page, remove all queries that might exist from previous purchase
    let currUrl = window.location.href;
    const index = window.location.href.lastIndexOf('?');
    if (index > -1) {
        currUrl = window.location.href.slice(0, index);
    }
    return currUrl;
};
